import { useState, useMemo, useCallback, type ReactNode } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { cn } from "@pma/utils/helpers"
import { ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react"
import { Button } from "@components/common/button"
import useTranslator from "@hooks/use-translator"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ColumnDef {
  header: string
  accessor: string
  sortable?: boolean
  render?: (value: any, row: any) => ReactNode
}

export interface DrillDownTableProps {
  columns: ColumnDef[]
  data: any[]
  onRowClick?: (row: any) => void
  maxRows?: number
}

// ---------------------------------------------------------------------------
// DrillDownTable — sortable data table with "show more" pagination
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 50

export function DrillDownTable({
  columns,
  data,
  onRowClick,
  maxRows,
}: DrillDownTableProps) {
  const { locale, translate } = useTranslator()
  const isHebrew = locale === "he"

  // Sort state
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  // Pagination
  const pageSize = maxRows ?? DEFAULT_PAGE_SIZE
  const [visibleCount, setVisibleCount] = useState(pageSize)

  const handleSort = useCallback((accessor: string) => {
    setSortCol((prev) => {
      if (prev === accessor) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return accessor
      }
      setSortDir("desc")
      return accessor
    })
  }, [])

  const sortedData = useMemo(() => {
    if (!sortCol) return data
    const sorted = [...data]
    sorted.sort((a, b) => {
      const aVal = a[sortCol]
      const bVal = b[sortCol]
      let cmp = 0
      if (typeof aVal === "number" && typeof bVal === "number") {
        cmp = aVal - bVal
      } else if (typeof aVal === "string" && typeof bVal === "string") {
        cmp = aVal.localeCompare(bVal, isHebrew ? "he" : "en")
      } else {
        cmp = String(aVal ?? "").localeCompare(String(bVal ?? ""))
      }
      return sortDir === "asc" ? cmp : -cmp
    })
    return sorted
  }, [data, sortCol, sortDir, isHebrew])

  const visibleData = sortedData.slice(0, visibleCount)
  const hasMore = visibleCount < sortedData.length

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {columns.map((col) => (
                <TableHead key={col.accessor} className="text-start">
                  {col.sortable !== false ? (
                    <button
                      onClick={() => handleSort(col.accessor)}
                      className="flex items-center gap-1 text-xs font-semibold hover:text-foreground transition-colors"
                    >
                      {col.header}
                      {sortCol === col.accessor ? (
                        sortDir === "asc" ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-40" />
                      )}
                    </button>
                  ) : (
                    <span className="text-xs font-semibold">{col.header}</span>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleData.map((row, rowIdx) => (
              <TableRow
                key={row.id ?? rowIdx}
                className={cn(
                  "transition-colors",
                  onRowClick && "cursor-pointer hover:bg-gray-50/80",
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => {
                  const val = row[col.accessor]
                  return (
                    <TableCell key={col.accessor} className="text-sm">
                      {col.render ? col.render(val, row) : (val ?? "-")}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setVisibleCount((prev) => prev + pageSize)}
            className="text-xs text-muted-foreground"
          >
            {translate("Show more")} ({sortedData.length - visibleCount} {translate("remaining")})
          </Button>
        </div>
      )}
    </div>
  )
}
